import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import ApiRequest from "../../helper/ApiRequest";
import CustomSelect from "../../component/CustomSelect";
import CustomImageUpload from "../../component/CustomImageUpload";
import DynamicCKEditor from "../../component/DynamicCkEditor";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";

const addBlogEndpoint = ApiEndpoints.aiBlog.blogCreate;
const authorListEndpoint = ApiEndpoints.autor.authorList;
const categoryListEndpoint = ApiEndpoints.category.categoryList;

export default function AddAiBlog() {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);

  const [previews, setPreviews] = useState({ thumbnail: null, banner_image: null, image1: null, image2: null });

  const [postData, setPostData] = useState({
    category_id: "", author_id: "", name: "", slug: "", thumbnail: null, alt_thumbnail: "", banner_image: null, alt_banner_image: "", heading1: "", description1: "", heading2: "", description2: "", heading3: "", description3: "", heading4: "", description4: "", heading5: "", description5: "", heading6: "", description6: "", heading7: "", description7: "", image1: null, alt_image1: "", image2: null, alt_image2: "", date: new Date(), faqs: [
      { question: "", answer: "" },
      { question: "", answer: "" },
    ]
  });

  useEffect(() => {

    const fetchCategories = async () => {
      try {
        const response = await ApiRequest("GET", categoryListEndpoint);
        setCategories(response?.blog_categories || []);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setCategories([]);
      }
    };

    const fetchAuthors = async () => {
      try {
        const response = await ApiRequest("GET", authorListEndpoint);
        setAuthors(response?.authors || []);
      } catch (error) {
        console.error("Failed to fetch authors:", error);
        setAuthors([]);
      }
    };

    fetchCategories();
    fetchAuthors();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];
      // Update the previews state
      setPreviews((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
      }));

      // Update the postData state
      setPostData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } else {
      // For non-file inputs, update the postData state directly
      setPostData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };


  const handleEditorChange = (key, data) => {
    setPostData((prevState) => ({
      ...prevState,
      [key]: data,
    }));
  };


  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const [key, value] of Object.entries(postData)) {
      if (value === "" || value === null) {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }

    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => {
      if (key === "faqs") {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });

    try {
      const response = await ApiRequest("POST", addBlogEndpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(response.message);
      navigate("/ai-blog");
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  // 🔹 handle change for question/answer
  const handleFaqChange = (index, field, value) => {
    setPostData((prev) => {
      const updatedFaqs = [...prev.faqs];
      updatedFaqs[index][field] = value;
      return { ...prev, faqs: updatedFaqs };
    });
  };

  // 🔹 add new FAQ
  const addFaq = () => {
    setPostData((prev) => ({
      ...prev,
      faqs: [...prev.faqs, { question: "", answer: "" }],
    }));
  };

  // 🔹 remove FAQ
  const removeFaq = (index) => {
    setPostData((prev) => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index),
    }));
  };

  const pagename = {
    name: 'Blog Management',
    user: 'Admin',
    innerpage: 'Blog Management / AI-Blog / Add Blog'
  }

  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        <form id="aiblogAdd" onSubmit={handleSubmit}>

          <div className="add-content-wrap">
            <h5>Title Section</h5>
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>ai blog title<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="name" type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>slug<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="slug" type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>category<small>*</small></label>
                  </div>
                  <CustomSelect onChange={handleChange} name={"category_id"} options={categories} placeholder="Select Category" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Date<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="date" type="date" className="form-control" placeholder="date" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Author Name<small>*</small></label>
                  </div>
                  <CustomSelect onChange={handleChange} name="author_id" options={authors} placeholder="Select Author Name" />
                </div>
              </div>

            </div>
          </div>

          <div className="add-content-wrap">
            <h5>Content Section</h5>
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 1<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="heading1" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description1<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description1} onChange={(event, editor) => handleEditorChange("description1", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 2<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="heading2" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description2<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description2} onChange={(event, editor) => handleEditorChange("description2", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 3<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="heading3" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description3<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description3} onChange={(event, editor) => handleEditorChange("description3", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 4<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="heading4" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description4<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description4} onChange={(event, editor) => handleEditorChange("description4", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 5<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="heading5" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description5<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description5} onChange={(event, editor) => handleEditorChange("description5", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 6<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="heading6" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description6<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description6} onChange={(event, editor) => handleEditorChange("description6", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 7<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="heading7" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description7<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description7} onChange={(event, editor) => handleEditorChange("description7", editor.getData())} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <h5>Image Section</h5>
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Banner Image (1608 × 600)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="banner_image"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.banner_image}
                  />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Alt For Inner banner<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="alt_banner_image" type="text" className="form-control" placeholder="Enter Alt Tag" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Thumbnail (444 × 332)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="thumbnail"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.thumbnail} />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Alt For Thumbnail<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="alt_thumbnail" type="text" className="form-control" placeholder="Enter Alt Tag" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>First Inner Image (1060 × 500)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="image1"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.image1}
                  />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Alt For First Inner Image<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="alt_image1" type="text" className="form-control" placeholder="Enter Alt Tag" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Second Inner Image (1060 × 500)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="image2"
                    // maxSize={550 * 1024}
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.image2} />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Alt For Second Inner Image<small>*</small></label>
                  </div>
                  <input onChange={handleChange} name="alt_image2" type="text" className="form-control" placeholder="Enter Alt Tag" />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <h5>Faq Section</h5>
            <div className="row">

              {postData.faqs.map((faq, index) => (
                <div key={index} className="col-lg-6" style={{ position: "relative" }}>
                  <div className="add-page-inputs input-add-image">
                    <div className="label-wrap-add">
                      <label>Question<small>*</small></label>
                    </div>
                    <input required className="form-control" type="text" placeholder={`Question ${index + 1}`} value={faq.question} onChange={(e) => handleFaqChange(index, "question", e.target.value)} />
                  </div>

                  <div className="add-page-inputs input-add-image">
                    <div className="label-wrap-add">
                      <label>Answer<small>*</small></label>
                    </div>
                    <input required className="form-control" placeholder={`Answer ${index + 1}`}
                      value={faq.answer} onChange={(e) => handleFaqChange(index, "answer", e.target.value)} />
                  </div>
                  {index >= 2 && (
                    <button type="button" style={{ position: "absolute", background: "linear-gradient(270deg,#e20814,#a80008)", top: "5px", right: "10px", color: "#fff", border: "none", borderRadius: "8px", fontSize: "12px", padding: "3px 15px" }} onClick={() => removeFaq(index)}>
                      REMOVE
                    </button>
                  )}
                </div>
              ))}

              <div className="col-lg-12 text-center">
                <button type="button" className="add-faqs" onClick={addFaq} style={{ background: "green", top: "5px", right: "10px", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", padding: "5px 15px", marginTop: "15px" }}>
                  Add New Faq
                </button>
              </div>
            </div>
          </div>
        </form>

        <button type="submit" form="aiblogAdd" className="default-btn">publish <span></span></button>
      </div>
    </>
  );
}
